"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, CreditCard, Sparkles, TrendingUp } from "lucide-react";
import Link from "next/link";
import { useApi } from "@/lib/fetch-client";
import { formatCredits } from "@/lib/format-credits";
import { isOrganizationAdmin } from "@llmgateway/shared/organization-roles";
import { TopUpCreditsDialog } from "./top-up-credits-dialog";
import type { Organization } from "@/lib/types";
interface CreditsDisplayProps {
  organization: Organization | null;
  isLoading?: boolean;
  // True when showing the dedicated chat-plan ("Chat plan") context rather than a
  // real dashboard org. The chat plan and its upgrade nudge only apply here.
  isChatPlanOrg?: boolean;
}
export function CreditsDisplay(t0) {
  const $ = _c(35);
  const {
    organization,
    isLoading,
    isChatPlanOrg: t1
  } = t0;
  const isChatPlanOrg = t1 === undefined ? false : t1;
  const api = useApi();
  let t2;
  if ($[0] !== api) {
    t2 = api.queryOptions("get", "/chat-plans/status");
    $[0] = api;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = Boolean(organization) && isChatPlanOrg;
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = {
      ...t2,
      enabled: t3,
      staleTime: 30000
    };
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const planQuery = useQuery(t4);
  const plan = planQuery.data;
  const hasActivePlan = isChatPlanOrg && plan && plan.chatPlan !== "none";
  if (!isChatPlanOrg && !isOrganizationAdmin(organization?.role)) {
    return null;
  }
  if (isLoading) {
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <CreditCard className="h-4 w-4 text-muted-foreground" />;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="px-2 py-1.5"><div className="w-full flex items-center justify-between p-2 rounded-md"><div className="flex items-center gap-2">{t5}<div className="flex flex-col"><span className="text-sm font-medium">Credits</span><span className="text-xs text-muted-foreground">Loading...</span></div></div></div></div>;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    return t6;
  }
  let t5;
  if ($[7] !== organization) {
    t5 = organization ? Number(organization.credits).toFixed(2) : "0.00";
    $[7] = organization;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const creditsBalance = t5;
  const planRemaining = hasActivePlan ? Number(plan.chatPlanCreditsRemaining) : 0;
  const totalAvailable = Number(creditsBalance) + planRemaining;
  const isLowCredits = organization && totalAvailable < 1;
  const hasNoCredits = organization && totalAvailable <= 0;
  let t6;
  if ($[9] !== hasActivePlan || $[10] !== plan || $[11] !== planRemaining) {
    t6 = hasActivePlan && plan && <Link href="/pricing" className="mb-1 block rounded-md border border-lounge-gold/25 bg-lounge-gold/[0.06] px-2 py-1.5 hover:bg-lounge-gold/10 transition-colors"><div className="flex items-center justify-between gap-2"><div className="flex items-center gap-2 min-w-0"><Sparkles className="h-3.5 w-3.5 shrink-0 text-lounge-gold" /><div className="flex flex-col min-w-0"><span className="text-xs font-medium truncate">{plan.chatPlan[0].toUpperCase() + plan.chatPlan.slice(1)}{" "}membership</span><span className="text-[10px] text-muted-foreground tabular-nums">${formatCredits(planRemaining)} of ${formatCredits(Number(plan.chatPlanCreditsLimit))} left</span></div></div></div></Link>;
    $[9] = hasActivePlan;
    $[10] = plan;
    $[11] = planRemaining;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== creditsBalance || $[14] !== hasNoCredits || $[15] !== isChatPlanOrg || $[16] !== isLowCredits || $[17] !== organization?.id) {
    t7 = !isChatPlanOrg && <TopUpCreditsDialog organizationId={organization?.id}><button className="w-full flex items-center justify-between p-2 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-left"><div className="flex items-center gap-2"><CreditCard className={`h-4 w-4 ${hasNoCredits ? "text-destructive" : isLowCredits ? "text-yellow-500" : "text-muted-foreground"}`} /><div className="flex flex-col"><span className="text-sm font-medium">Credits</span><span className={`text-xs ${hasNoCredits ? "text-destructive" : isLowCredits ? "text-yellow-600" : "text-muted-foreground"}`}>${creditsBalance}</span></div></div><span className="text-xs text-muted-foreground">Add</span></button></TopUpCreditsDialog>;
    $[13] = creditsBalance;
    $[14] = hasNoCredits;
    $[15] = isChatPlanOrg;
    $[16] = isLowCredits;
    $[17] = organization?.id;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== hasActivePlan || $[20] !== isChatPlanOrg) {
    t8 = isChatPlanOrg && !hasActivePlan && <Link href="/pricing" className="group mt-1 flex items-center gap-2.5 rounded-md border border-lounge-gold/25 bg-lounge-gold/[0.06] px-2 py-2 transition-colors hover:bg-lounge-gold/10"><span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-lounge-gold/15 text-lounge-gold"><TrendingUp className="size-3.5" /></span><div className="flex min-w-0 flex-col"><span className="text-xs font-medium leading-tight">Get up to 3× the credits</span><span className="text-[10px] text-muted-foreground leading-tight">Become a member · from $9/mo</span></div><ChevronRight className="ml-auto size-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" /></Link>;
    $[19] = hasActivePlan;
    $[20] = isChatPlanOrg;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== hasNoCredits || $[23] !== isChatPlanOrg) {
    t9 = !isChatPlanOrg && hasNoCredits && <div className="mt-1 px-2"><p className="text-xs text-destructive">⚠️ No credits remaining</p></div>;
    $[22] = hasNoCredits;
    $[23] = isChatPlanOrg;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== hasNoCredits || $[26] !== isChatPlanOrg || $[27] !== isLowCredits) {
    t10 = !isChatPlanOrg && isLowCredits && !hasNoCredits && <div className="mt-1 px-2"><p className="text-xs text-yellow-600">⚡ Low credits remaining</p></div>;
    $[25] = hasNoCredits;
    $[26] = isChatPlanOrg;
    $[27] = isLowCredits;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t11 = <div className="px-2 py-1.5">{t6}{t7}{t8}{t9}{t10}</div>;
    $[29] = t10;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
}